/**
 * Financial Glossary
 * Central repository of financial term definitions for info tooltips
 * Includes Canadian tax form line references where applicable
 */

export interface GlossaryTerm {
  term: string;
  shortDefinition: string;
  fullDefinition: string;
  taxLine?: string;  // CRA line number if applicable
  relatedTerms?: string[];
  examples?: string[];
}

export const financialGlossary: Record<string, GlossaryTerm> = {
  // Income Types
  'employment_income': {
    term: 'Employment Income',
    shortDefinition: 'Salary, wages, and bonuses from your employer',
    fullDefinition: 'Employment income includes all compensation from your employer: salary, wages, bonuses, commissions, tips, and taxable benefits. This is reported on your T4 slip.',
    taxLine: 'Line 10100/10400',
    relatedTerms: ['t4', 'gross_income'],
    examples: ['Annual salary: $75,000', 'Bonus: $5,000', 'Taxable car allowance: $3,600']
  },
  'self_employment_income': {
    term: 'Self-Employment Income',
    shortDefinition: 'Business income from freelancing or your own business',
    fullDefinition: 'Net income from self-employment after deducting business expenses. Self-employed individuals pay both the employee and employer portions of CPP (11.9% total) but EI is optional.',
    taxLine: 'Line 13500-14300',
    relatedTerms: ['cpp', 'business_expenses'],
  },
  'other_income': {
    term: 'Other Income',
    shortDefinition: 'Pension, interest, rental income, and other taxable income',
    fullDefinition: 'Includes pension income (T4A), RRIF withdrawals, interest income (T5), rental income, and other taxable amounts not from employment.',
    taxLine: 'Line 12900/13000',
    relatedTerms: ['interest_income', 'pension_income'],
  },
  'capital_gains': {
    term: 'Capital Gains',
    shortDefinition: 'Profit from selling investments (50% taxable)',
    fullDefinition: 'When you sell an investment (stocks, real estate, etc.) for more than you paid, the profit is a capital gain. Only 50% of capital gains are taxable in Canada. Track your adjusted cost base (ACB) for accurate reporting.',
    taxLine: 'Schedule 3',
    relatedTerms: ['capital_loss', 'acb'],
    examples: ['Sold shares for $10,000 (cost $6,000) = $4,000 gain, $2,000 taxable']
  },
  'eligible_dividends': {
    term: 'Eligible Dividends',
    shortDefinition: 'Dividends from Canadian public corporations (tax-advantaged)',
    fullDefinition: 'Eligible dividends from Canadian public corporations receive preferential tax treatment. They are "grossed up" by 38% for tax calculation, then you receive a dividend tax credit. At lower incomes, dividends can be nearly tax-free.',
    taxLine: 'Line 12000',
    relatedTerms: ['dividend_tax_credit', 'gross_up'],
  },

  // Deductions
  'rrsp': {
    term: 'RRSP (Registered Retirement Savings Plan)',
    shortDefinition: 'Tax-deferred retirement savings account',
    fullDefinition: 'RRSP contributions reduce your taxable income dollar-for-dollar. Tax is deferred until withdrawal in retirement (hopefully at a lower tax rate). 2024 contribution limit: 18% of previous year earned income, max $31,560.',
    taxLine: 'Line 20800',
    relatedTerms: ['rrsp_deduction_limit', 'tfsa'],
    examples: ['$10,000 contribution at 40% marginal rate = $4,000 tax savings']
  },
  'rrsp_contribution': {
    term: 'RRSP Contribution',
    shortDefinition: 'Amount added to your RRSP (tax-deductible)',
    fullDefinition: 'RRSP contributions are deducted from your income, reducing your taxes at your marginal rate. Unused contribution room carries forward. Check your Notice of Assessment for your limit.',
    taxLine: 'Line 20800',
    relatedTerms: ['rrsp', 'marginal_rate'],
  },
  'tfsa': {
    term: 'TFSA (Tax-Free Savings Account)',
    shortDefinition: 'Tax-free growth savings account',
    fullDefinition: 'TFSA contributions are not tax-deductible, but all growth and withdrawals are completely tax-free. Annual limit: $7,000 (2024). Unlike RRSP, withdrawals do not affect government benefits like OAS.',
    relatedTerms: ['rrsp'],
  },

  // CPP & EI
  'cpp': {
    term: 'CPP (Canada Pension Plan)',
    shortDefinition: 'Mandatory retirement pension contributions',
    fullDefinition: 'CPP is a mandatory pension program for workers. Employee rate: 5.95% (2024). Self-employed pay both portions (11.9%). Contributions build your future CPP retirement pension. Basic exemption: $3,500.',
    relatedTerms: ['cpp2', 'ympe'],
  },
  'cpp2': {
    term: 'CPP2 (Enhanced CPP)',
    shortDefinition: 'Additional CPP on income above YMPE',
    fullDefinition: 'Starting 2024, additional 4% CPP contribution on earnings between YMPE ($68,500) and YAMPE ($73,200). This enhances your future CPP benefits.',
    relatedTerms: ['cpp', 'ympe', 'yampe'],
  },
  'ei': {
    term: 'EI (Employment Insurance)',
    shortDefinition: 'Insurance for job loss and other benefits',
    fullDefinition: 'EI premiums provide income support if you lose your job, plus maternity/parental leave benefits. Rate: 1.66% (2024) up to max insurable earnings of $63,200. Self-employed can opt in for special benefits.',
    relatedTerms: ['ei_premium'],
  },
  'ympe': {
    term: 'YMPE (Year\'s Maximum Pensionable Earnings)',
    shortDefinition: 'Maximum earnings subject to base CPP',
    fullDefinition: 'The YMPE is the maximum earnings subject to base CPP contributions. For 2024: $68,500. Income above this amount (up to YAMPE) is subject to CPP2.',
  },
  'yampe': {
    term: 'YAMPE (Year\'s Additional Maximum Pensionable Earnings)',
    shortDefinition: 'Maximum earnings for enhanced CPP (CPP2)',
    fullDefinition: 'Income between YMPE and YAMPE is subject to the enhanced CPP2 contribution (4%). For 2024: $73,200.',
  },

  // Tax Concepts
  'marginal_rate': {
    term: 'Marginal Tax Rate',
    shortDefinition: 'Tax rate on your next dollar of income',
    fullDefinition: 'Your marginal rate is the tax rate applied to your next dollar of income. It is the sum of your federal and provincial rates at your income level. This is the rate you save when making RRSP contributions.',
    relatedTerms: ['effective_rate', 'tax_brackets'],
    examples: ['At $100k income in Ontario: 43.41% marginal rate']
  },
  'effective_rate': {
    term: 'Effective Tax Rate',
    shortDefinition: 'Your average tax rate (total tax / income)',
    fullDefinition: 'Your effective (average) tax rate is your total tax divided by your total income. It is always lower than your marginal rate due to progressive tax brackets.',
    relatedTerms: ['marginal_rate'],
    examples: ['$25,000 tax on $100,000 income = 25% effective rate']
  },
  'tax_brackets': {
    term: 'Tax Brackets',
    shortDefinition: 'Income ranges with different tax rates',
    fullDefinition: 'Canada uses progressive tax brackets where higher income is taxed at higher rates. You pay the lower rate on income up to each threshold, not on all income.',
    relatedTerms: ['marginal_rate', 'progressive_tax'],
  },
  'basic_personal_amount': {
    term: 'Basic Personal Amount (BPA)',
    shortDefinition: 'Tax-free income amount for all Canadians',
    fullDefinition: 'The BPA is the amount of income everyone can earn tax-free. Federal BPA (2024): $15,705. Each province has its own BPA. You receive a tax credit equal to BPA × lowest bracket rate.',
    taxLine: 'Line 30300',
  },
  'dividend_tax_credit': {
    term: 'Dividend Tax Credit',
    shortDefinition: 'Credit that reduces tax on Canadian dividends',
    fullDefinition: 'To avoid double-taxation of corporate profits, dividends are "grossed up" then you receive a tax credit. For eligible dividends: 38% gross-up, 15.02% federal credit. This makes dividends tax-efficient at lower incomes.',
    relatedTerms: ['eligible_dividends', 'gross_up'],
  },
  'gross_up': {
    term: 'Dividend Gross-Up',
    shortDefinition: 'Increasing dividend amount for tax calculation',
    fullDefinition: 'Eligible dividends are multiplied by 1.38 (grossed up 38%) for tax calculation. This represents the pre-tax corporate earnings. The dividend tax credit then offsets the extra tax.',
    relatedTerms: ['dividend_tax_credit', 'eligible_dividends'],
  },

  // Retirement
  'rrif': {
    term: 'RRIF (Registered Retirement Income Fund)',
    shortDefinition: 'RRSP converted to retirement income',
    fullDefinition: 'You must convert your RRSP to a RRIF by December 31 of the year you turn 71. RRIFs have minimum annual withdrawals (increasing with age) that are fully taxable.',
    relatedTerms: ['rrsp', 'minimum_withdrawal'],
  },
  'oas': {
    term: 'OAS (Old Age Security)',
    shortDefinition: 'Government pension for seniors 65+',
    fullDefinition: 'OAS is a monthly benefit for Canadians 65+. Maximum (2024): ~$727/month. Subject to clawback if income exceeds ~$90,997. Can be deferred to age 70 for 36% increase.',
    relatedTerms: ['oas_clawback', 'gis'],
  },
  'oas_clawback': {
    term: 'OAS Clawback',
    shortDefinition: 'OAS reduction for high-income seniors',
    fullDefinition: 'If your net income exceeds ~$90,997 (2024), OAS is reduced by 15% of income above this threshold. At ~$148,451, OAS is fully clawed back. TFSA withdrawals do not trigger clawback.',
    relatedTerms: ['oas'],
  },
  'cpp_pension': {
    term: 'CPP Retirement Pension',
    shortDefinition: 'Monthly pension from CPP contributions',
    fullDefinition: 'Based on your lifetime CPP contributions. Can start at 60 (36% reduction), 65 (standard), or defer to 70 (42% increase). Maximum at 65 (2024): ~$1,364/month.',
    relatedTerms: ['cpp'],
  },
  'gis': {
    term: 'GIS (Guaranteed Income Supplement)',
    shortDefinition: 'Extra benefit for low-income seniors',
    fullDefinition: 'GIS provides additional income for low-income OAS recipients. Income-tested and tax-free. Maximum single (2024): ~$1,065/month. Reduced as other income increases.',
    relatedTerms: ['oas'],
  },
  'lira': {
    term: 'LIRA (Locked-In Retirement Account)',
    shortDefinition: 'Locked pension funds from former employer',
    fullDefinition: 'LIRAs hold pension funds transferred from employer pension plans. Funds are "locked in" until retirement. Must convert to LIF by December 31 of the year you turn 71. Some provinces allow partial unlocking.',
    relatedTerms: ['lif', 'pension'],
  },
  'lif': {
    term: 'LIF (Life Income Fund)',
    shortDefinition: 'Income fund from locked-in pension',
    fullDefinition: 'LIFs are created when you convert a LIRA at retirement. They have both minimum and maximum annual withdrawal limits. Withdrawals are fully taxable as income.',
    relatedTerms: ['lira', 'rrif'],
  },

  // Housing
  'cmhc_insurance': {
    term: 'CMHC Insurance',
    shortDefinition: 'Mortgage insurance for down payments under 20%',
    fullDefinition: 'If your down payment is less than 20%, you must pay mortgage default insurance (CMHC, Sagen, or Canada Guaranty). Premium: 2.8-4% of mortgage added to your loan.',
  },
  'land_transfer_tax': {
    term: 'Land Transfer Tax',
    shortDefinition: 'Provincial tax when buying property',
    fullDefinition: 'One-time tax paid when purchasing property. Rates vary by province (Ontario: 0.5-2.5%, BC: 1-5%). Toronto has additional municipal LTT. First-time buyers may get rebates.',
  },
  'cap_rate': {
    term: 'Cap Rate (Capitalization Rate)',
    shortDefinition: 'Property\'s annual return as percentage of value',
    fullDefinition: 'Cap rate = Net Operating Income / Property Value. Used to compare investment properties. Higher cap rate = higher risk/return. Typical residential: 3-6%.',
    examples: ['$24,000 NOI / $400,000 value = 6% cap rate']
  },

  // Investment
  'compound_interest': {
    term: 'Compound Interest',
    shortDefinition: 'Interest earned on interest',
    fullDefinition: 'Compound interest is when you earn returns on both your principal and accumulated interest. Over time, this creates exponential growth. The Rule of 72: divide 72 by your return rate to estimate doubling time.',
    examples: ['$10,000 at 7% doubles in ~10 years (72/7)']
  },
  'acb': {
    term: 'ACB (Adjusted Cost Base)',
    shortDefinition: 'Your cost basis for capital gains calculation',
    fullDefinition: 'ACB is your total investment cost including purchase price, commissions, and reinvested distributions. Capital gain = Proceeds - ACB. Track carefully for tax reporting.',
    relatedTerms: ['capital_gains'],
  },

  // Tax Slips Reference
  't4': {
    term: 'T4 Slip',
    shortDefinition: 'Employment income statement from employer',
    fullDefinition: 'T4 shows your employment income, tax withheld, CPP/EI contributions, and taxable benefits. Employers must issue by end of February for the previous tax year.',
    relatedTerms: ['employment_income'],
  },
  't5': {
    term: 'T5 Slip',
    shortDefinition: 'Investment income statement',
    fullDefinition: 'T5 reports investment income: interest, dividends, and other income from Canadian sources. Issued by banks, brokerages, and corporations.',
    taxLine: 'Line 12000 (dividends), Line 12100 (interest)',
    relatedTerms: ['interest_income', 'eligible_dividends'],
  },
  't4a': {
    term: 'T4A Slip',
    shortDefinition: 'Pension and other income statement',
    fullDefinition: 'T4A reports pension income, scholarships, self-employed commissions, RESP/RRSP withdrawals, and other income not on T4.',
    relatedTerms: ['pension_income'],
  },
};

/**
 * Get a glossary term by key
 */
export const getTerm = (key: string): GlossaryTerm | undefined => {
  return financialGlossary[key.toLowerCase()];
};

/**
 * Search glossary terms by keyword
 */
export const searchGlossary = (query: string): GlossaryTerm[] => {
  const lowerQuery = query.toLowerCase();
  return Object.values(financialGlossary).filter(term =>
    term.term.toLowerCase().includes(lowerQuery) ||
    term.shortDefinition.toLowerCase().includes(lowerQuery) ||
    term.fullDefinition.toLowerCase().includes(lowerQuery)
  );
};

export default financialGlossary;
